import React, { useRef, useState, useEffect } from 'react';
import { InteractiveImageObj, ButtonObj as OriginalButtonObj } from '../empracoTypes';

interface ButtonObj extends OriginalButtonObj {
  buttonRef: React.RefObject<HTMLDivElement>;
  featureInfoRef: React.RefObject<HTMLDivElement>;
}

export default function InteractiveImage(props: InteractiveImageObj) {
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1200);
  const [mainContainerRect, setMainContainerRect] = useState(mainContainerRef.current?.getBoundingClientRect())
  const [isFeatureActive, setIsFeatureActive] = useState(false);
  const [points, setPoints] = useState("0,0 0,0 0,0");
  const [buttons, setButtons] = React.useState<ButtonObj[]>(props.buttons.map((button) => ({
    ...button,
    buttonRef: React.createRef<HTMLDivElement>(),
    featureInfoRef: React.createRef<HTMLDivElement>(),
  })));

  const handleResize = () => {
    setIsMobile(window.innerWidth < 1200);
  };

  const handleFeatureInfo = (e: React.MouseEvent, clickedButton: ButtonObj) => {
    e.preventDefault();
    setButtons((prevButtons) =>
      prevButtons.map((button) => ({
        ...button,
        featureInput: {
          ...button.featureInput,
          active: button.id === clickedButton.id ? !button.featureInput.active : false,
        },
      }))
    );
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const activeButton = buttons.find((button) => button.featureInput.active);
    if (activeButton) {
      const activeButtonRect = activeButton.buttonRef.current?.getBoundingClientRect();
      const featureInfoClickedRect = activeButton.featureInfoRef.current?.getBoundingClientRect();

      if (mainContainerRect && activeButtonRect && featureInfoClickedRect) {
        const buttonLeft = activeButton.positionX < 0.5;

        const startPointX = buttonLeft || isMobile ? activeButtonRect.left - mainContainerRect.left - 6 : activeButtonRect.left - mainContainerRect.left + 36;
        const startPointY = (activeButtonRect.top + activeButtonRect.height / 2) - mainContainerRect.top;

        const connectPointX = buttonLeft || isMobile ? featureInfoClickedRect.right - mainContainerRect.left : featureInfoClickedRect.left - mainContainerRect.left;
        const connectPointY = featureInfoClickedRect.bottom - mainContainerRect.top;

        const endPointX = buttonLeft || isMobile ? featureInfoClickedRect.left - mainContainerRect.left : featureInfoClickedRect.right - mainContainerRect.left;
        const endPointY = featureInfoClickedRect.bottom - mainContainerRect.top;

        const updatedPoints = `${startPointX},${startPointY} ${connectPointX},${connectPointY} ${endPointX},${endPointY}`;
        setPoints(updatedPoints);
      }
    }
    setIsFeatureActive(!!activeButton);
  }, [buttons, mainContainerRect, isMobile]);

  useEffect(() => {
    setMainContainerRect(mainContainerRef.current?.getBoundingClientRect());
  }, [buttons]); 

  return (
    <div className="empori-interactive-image-grid" ref={mainContainerRef}>
      {isFeatureActive && (
        <svg
          className="lines-svg"
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          version="1.1"
          id="Layer_1"
          viewBox={`0 0 ${mainContainerRect?.width} ${mainContainerRect?.height}`}
          preserveAspectRatio="xMinYMin meet"
          width="100%"
          height="100%"
        >
          <polyline className="st0" points={points} />
        </svg>
      )}

      <div className="feature-info-container left">
        {isMobile
          ? buttons.map((button) => (
              <div
                key={button.id}
                className={`feature-info ${button.featureInput.active ? 'clicked' : ''}`}
                ref={button.featureInfoRef}
                dangerouslySetInnerHTML={{ __html: button.featureInput.inputText }}
              ></div>
            ))
          : buttons
              .filter((button) => button.positionX < 0.5)
              .map((button) => (
                <div
                  key={button.id}
                  className={`feature-info ${button.featureInput.active ? 'clicked' : ''}`}
                  ref={button.featureInfoRef}
                  dangerouslySetInnerHTML={{ __html: button.featureInput.inputText }}
                ></div>
              ))}
      </div>

      <div className="image-container">
        <img className="product-image" src={props.image || ''} alt={props.altText} />
          {buttons.map((button) => (
            <div
              key={button.id}
              className={`button feature-point ${button.featureInput.active ? 'active' : ''}`}
              style={{ left: `${button.positionX * 100}%`, top: `${button.positionY * 100}%` }}
              onClick={(e) => handleFeatureInfo(e, button)}
              ref={button.buttonRef}
            >
              {button.featureInput.active ? (
                <svg width="37" height="3" viewBox="0 0 37 3" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <line y1="1.5" x2="37" y2="1.5" stroke="#BF8D43" strokeWidth="3" />
                </svg>
              ) : (
                <svg width="37" height="37" viewBox="0 0 37 37" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <line y1="18.5" x2="37" y2="18.5" stroke="#fff" strokeWidth="3" />
                  <line x1="18.5" y1="37" x2="18.5" stroke="#fff" strokeWidth="3" />
                </svg>
              )}
            </div>
          ))}
      </div>

      {!isMobile && (
        <div className="feature-info-container right">
          {buttons
            .filter((button) => button.positionX >= 0.5)
            .map((button) => (
              <div
                key={button.id}
                className={`feature-info ${button.featureInput.active ? 'clicked' : ''}`}
                ref={button.featureInfoRef}
                dangerouslySetInnerHTML={{ __html: button.featureInput.inputText }}
              ></div>
            ))}
        </div>
      )}
      
    </div>
  );
}